import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from './styles.js';
import type { SortDirection, SortState } from './types.js';
import '@triptease/tt-paginator';

const ARIA_SORT_MAP: Record<SortDirection, string> = {
  asc: 'ascending',
  desc: 'descending',
  none: '',
};

const NEXT_DIRECTION: Record<SortDirection, SortDirection> = {
  none: 'asc',
  asc: 'desc',
  desc: 'none',
};


export class TtTableWrapper extends LitElement {
  static styles = styles;

  @property({ type: Number, attribute: 'total-rows' })
  totalRows = 0;

  @property({ type: Number, attribute: 'page-size' })
  pageSize = 10;

  private sortState: SortState = { key: '', direction: 'none' };
  private headerClickHandlers = new Map<HTMLElement, () => void>();
  private statusMessage = '';

  render() {
    return html`
      <div>
        <slot @slotchange=${this.handleSlotChange}></slot>
        <tt-paginator part="paginator" total-rows=${this.totalRows} page-size=${this.pageSize}></tt-paginator>
      </div>
      <div role="status" aria-live="polite" aria-atomic="true" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap">${this.statusMessage}</div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupHandlers();
  }

  private handleSlotChange() {
    this.cleanupHandlers();
    this.sortState = { key: '', direction: 'none' };

    const headers = this.querySelectorAll<HTMLElement>('th[data-sort-key]');
    for (const header of headers) {
      header.setAttribute('aria-sort', 'none');

      const button = document.createElement('button');
      while (header.firstChild) {
        button.appendChild(header.firstChild);
      }
      header.appendChild(button);

      const handler = () => this.handleSortClick(header);
      this.headerClickHandlers.set(header, handler);
      button.addEventListener('click', handler);
    }
  }

  private handleSortClick(header: HTMLElement) {
    const key = header.dataset.sortKey!;
    const isSameColumn = key === this.sortState.key;

    const direction = isSameColumn ? NEXT_DIRECTION[this.sortState.direction] : 'asc';

    this.clearAriaSortFromAll();

    if (direction !== 'none') {
      header.setAttribute('aria-sort', ARIA_SORT_MAP[direction]);
    }

    const columnName = header.querySelector('button')?.textContent?.trim() ?? key;
    this.statusMessage = direction === 'none'
      ? `Sort removed from ${columnName}`
      : `Sorted by ${columnName}, ${ARIA_SORT_MAP[direction]}`;
    this.requestUpdate();

    this.sortState = { key, direction };

    this.dispatchEvent(
      new CustomEvent('sort-change', {
        detail: { key, direction },
        bubbles: true,
        composed: true,
      })
    );
  }

  private clearAriaSortFromAll() {
    const headers = this.querySelectorAll('th[aria-sort]');
    for (const header of headers) {
      header.setAttribute('aria-sort', 'none');
    }
  }

  private cleanupHandlers() {
    for (const [header, handler] of this.headerClickHandlers) {
      const button = header.querySelector('button');
      if (button) {
        button.removeEventListener('click', handler);
        while (button.firstChild) {
          header.insertBefore(button.firstChild, button);
        }
        button.remove();
      }
    }
    this.headerClickHandlers.clear();
  }
}
